import * as CSS from 'csstype';
import styled, { css } from 'styled-components';
import { colors, typography } from '../../settings';

import {
    Clickable,
    ClickableProps,
    ClickableWithRoutingProps,
    ClickableNoRoutingProps,
    ClickableComponent,
} from '../Clickable';

type Underline = 'none' | 'hover';
interface LinkStylesProps {
    color?: CSS.Property.Color | colors.Color;
    /**
     * Sets the font-family CSS property based on typography settings
     */
    fontFamily?: keyof typeof typography.fontFamilies;
    /**
     * Sets the font-size CSS property based on typography settings
     */
    size?: keyof typeof typography.typeScale;
    /**
     * When to display an underline, defaults to always show
     */
    underline?: Underline;
}

export type LinkWithRoutingProps = ClickableWithRoutingProps<LinkStylesProps>;
export type LinkNoRoutingProps = ClickableNoRoutingProps<LinkStylesProps>;
export type LinkProps = ClickableProps<LinkStylesProps>;

export const Link = styled(Clickable)(linkStyles) as ClickableComponent<LinkStylesProps>;

export function linkStyles(props: LinkProps) {
    const { color = 'puntRoad', fontFamily = 'root', size = 'size3', underline } = props;

    const colorCode = colors.getColor(color);
    const isLink = 'href' in props && props.href !== undefined;

    return css`
        ${!isLink &&
        // Reset button styles
        css`
            border: none;
            background: none;
            padding: 0;
            margin: 0;
            cursor: pointer;
            appearance: none;
        `}

        font-family: ${typography.fontFamilies[fontFamily]};
        font-size: ${typography.typeScale[size]}px;
        line-height: inherit;

        &,
        &:hover,
        &:focus {
            color: ${colorCode};
            text-decoration: none;
        }

        ${getUnderlineStyles(colorCode, underline)}

        &[aria-disabled='true'] {
            border-color: ${colors.t1000};
            color: ${colors.pebbleBeach};
        }
    `;
}

function getUnderlineStyles(colorCode: string, underline?: Underline) {
    return css`
        border-bottom: 2px solid;
        transition: border-color 0.15s ease-out;

        ${() => {
            if (underline === 'none') {
                return css`
                    border-bottom: none;
                `;
            } else if (underline === 'hover') {
                return css`
                    &,
                    &:link,
                    &:visited {
                        border-color: transparent;
                    }
                    &:hover,
                    &:focus {
                        border-color: ${colorCode};
                    }
                `;
            } else {
                return css`
                    &,
                    &:link,
                    &:visited {
                        border-color: ${colors.withAlpha(colorCode, 0.2)};
                    }
                    &:hover,
                    &:focus {
                        border-color: ${colors.withAlpha(colorCode, 0.4)};
                    }
                `;
            }
        }}
    `;
}
